import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, API_BASE, getAccessToken } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, ChevronRight, Download, Edit3, ShieldCheck, Trash2, PencilLine, FileCheck, RefreshCw, FilePlus } from 'lucide-react';

interface Section {
  code: string;
  name: string;
  group?: string;
  description?: string;
  enabled: boolean;
  sum_insured_zar?: number | null;
  premium_zar?: number | null;
  deductible_zar?: number | null;
  indemnity_period_months?: number | null;
  notes?: string;
}

interface Cover {
  id: string;
  cover_number: string;
  status: 'draft' | 'issued';
  insured_name?: string;
  client_id?: string;
  client_name?: string;
  site_id?: string;
  site_name?: string;
  site_reference?: string;
  site_address?: string;
  broker_id?: string;
  broker_name?: string;
  broker_fsp?: string;
  policy_period_start?: string;
  policy_period_end?: string;
  sections: Section[];
  endorsements?: string[];
  warranties?: string[];
  notes?: string;
  totals?: { total_sum_insured_zar?: number; total_premium_zar?: number };
  issued_at?: string;
  created_at?: string;
}

const fmtZAR = (n?: number | null): string => {
  if (n == null || Number.isNaN(n)) return '—';
  return `R\u00a0${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (iso?: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return String(iso).slice(0, 10);
  }
};

export const CoverDetailPage: React.FC = () => {
  const { coverId } = useParams();
  const nav = useNavigate();
  const [cover, setCover] = useState<Cover | null>(null);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);

  const load = async () => {
    if (!coverId) return;
    setLoading(true);
    try {
      const r = await api.get<Cover>(`/covers/${coverId}`);
      setCover(r.data);
    } catch {
      toast.error('Failed to load cover');
    } finally { setLoading(false); }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [coverId]);

  const issue = async () => {
    if (!coverId) return;
    setIssuing(true);
    try {
      await api.post(`/covers/${coverId}/issue`);
      toast.success('Confirmation of Cover issued');
      void load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Issue failed';
      toast.error(msg);
    } finally { setIssuing(false); }
  };

  const remove = async () => {
    if (!coverId) return;
    if (!confirm('Delete this draft cover? This cannot be undone.')) return;
    try {
      await api.delete(`/covers/${coverId}`);
      toast.success('Draft deleted');
      nav('/insurer/covers');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Delete failed';
      toast.error(msg);
    }
  };

  const downloadPdf = async (kind: 'coc' | 'policy' = 'coc') => {
    if (!coverId) return;
    try {
      const token = getAccessToken();
      const path = kind === 'policy' ? 'policy-document/pdf' : 'pdf';
      const suffix = kind === 'policy' ? '-policy' : '';
      const r = await fetch(`${API_BASE}/covers/${coverId}/${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : undefined });
      if (!r.ok) throw new Error('PDF request failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${cover?.cover_number || coverId}${suffix}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const renew = async () => {
    if (!coverId) return;
    if (!confirm('Create a renewal draft carrying this cover forward by 12 months? The renewal will use the CURRENT rating table version.')) return;
    try {
      const r = await api.post<{ ok: boolean; id: string; cover_number: string }>(`/covers/${coverId}/renew`);
      toast.success(`Renewal draft created: ${r.data.cover_number}`);
      nav(`/insurer/covers/${r.data.id}/edit`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Renewal failed';
      toast.error(msg);
    }
  };

  const [showAddendum, setShowAddendum] = useState(false);

  if (loading) return <div className="text-raptor-navy/60 py-10 text-center">Loading…</div>;
  if (!cover) return <div className="text-raptor-navy/60 py-10 text-center">Cover not found.</div>;

  const enabled = cover.sections.filter((s) => s.enabled);
  const byGroup: Record<string, Section[]> = {};
  enabled.forEach((s) => {
    const g = s.group || 'Other';
    byGroup[g] = byGroup[g] || [];
    byGroup[g].push(s);
  });
  const groupOrder = ['Property', 'Motor', 'Liability', 'Other'];
  const groups = Object.keys(byGroup).sort((a, b) => (groupOrder.indexOf(a) === -1 ? 99 : groupOrder.indexOf(a)) - (groupOrder.indexOf(b) === -1 ? 99 : groupOrder.indexOf(b)));

  const isIssued = cover.status === 'issued';

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl" data-testid="cover-detail-page">
      <div className="flex items-center gap-1 text-xs font-body text-raptor-navy/60">
        <button type="button" onClick={() => nav('/insurer/covers')} className="hover:text-raptor-blue inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Confirmations of Cover
        </button>
        <ChevronRight size={12} className="text-raptor-navy/30" />
        <span className="text-raptor-navy">{cover.cover_number}</span>
      </div>

      {/* Hero card — branded schedule preview */}
      <div className="rounded-lg overflow-hidden shadow-sm border border-border/60">
        <div className="raptor-gradient px-6 py-5 text-white flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs tracking-widest opacity-80 uppercase">Confirmation of Cover</div>
            <div className="font-headline text-3xl tracking-wide mt-0.5">{cover.insured_name}</div>
            <div className="text-sm opacity-90 mt-1 font-body">
              {cover.client_name}
              {cover.site_name && <> · {cover.site_name}</>}
              {cover.site_reference && <> · <span className="font-tabular">{cover.site_reference}</span></>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest opacity-80">Cover №</div>
            <div className="font-tabular text-2xl font-bold tracking-wider">{cover.cover_number}</div>
            {isIssued ? (
              <Badge variant="success" className="mt-2"><ShieldCheck size={12} /> Issued {fmtDate(cover.issued_at)}</Badge>
            ) : (
              <Badge variant="warning" className="mt-2"><PencilLine size={12} /> Draft</Badge>
            )}
          </div>
        </div>

        {/* Period ribbon */}
        <div className="grid grid-cols-3 bg-raptor-navy text-white text-sm">
          <PeriodCell label="Period from" value={fmtDate(cover.policy_period_start)} />
          <PeriodCell label="To" value={fmtDate(cover.policy_period_end)} />
          <PeriodCell label="Broker" value={cover.broker_name || '—'} sub={cover.broker_fsp ? `FSP ${cover.broker_fsp}` : undefined} />
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        {!isIssued && (
          <>
            <Button variant="outline" onClick={() => nav(`/insurer/covers/${cover.id}/edit`)} data-testid="cover-edit-btn">
              <Edit3 size={14} className="mr-1" /> Edit draft
            </Button>
            <Button variant="gradient" onClick={() => void issue()} disabled={issuing} data-testid="cover-issue-btn">
              <ShieldCheck size={14} className="mr-1" /> {issuing ? 'Issuing…' : 'Issue cover'}
            </Button>
            <Button variant="destructive" onClick={() => void remove()} data-testid="cover-delete-btn">
              <Trash2 size={14} className="mr-1" /> Delete
            </Button>
          </>
        )}
        <Button variant={isIssued ? 'gradient' : 'outline'} onClick={() => void downloadPdf('coc')} data-testid="cover-pdf-btn">
          <Download size={14} className="mr-1" /> Confirmation of Cover (PDF)
        </Button>
        <Button variant="outline" onClick={() => void downloadPdf('policy')} data-testid="cover-policy-pdf-btn">
          <FileCheck size={14} className="mr-1" /> Full Policy Document (PDF)
        </Button>
        {isIssued && (
          <>
            <Button variant="outline" onClick={() => setShowAddendum(true)} data-testid="cover-addendum-btn">
              <FilePlus size={14} className="mr-1" /> Issue Addendum
            </Button>
            <Button variant="outline" onClick={() => void renew()} data-testid="cover-renew-btn">
              <RefreshCw size={14} className="mr-1" /> Renew
            </Button>
          </>
        )}
      </div>

      <AddendumModal open={showAddendum} onClose={() => setShowAddendum(false)} coverId={cover.id} onIssued={() => { setShowAddendum(false); void load(); }} />

      {/* Sections */}
      <Card>
        <CardHeader className="flex-row items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base">Sections of Cover</CardTitle>
            <CardDescription>{enabled.length} peril{enabled.length === 1 ? '' : 's'} enabled.</CardDescription>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-raptor-navy/60">Total sum insured / premium</div>
            <div className="font-tabular font-semibold text-raptor-navy">
              {fmtZAR(cover.totals?.total_sum_insured_zar)} <span className="text-raptor-navy/40">·</span> {fmtZAR(cover.totals?.total_premium_zar)}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {enabled.length === 0 ? (
            <div className="p-8 text-center text-raptor-navy/50 font-body text-sm">No peril sections enabled on this cover yet.</div>
          ) : (
            <table className="w-full text-sm font-body">
              <thead className="bg-raptor-neutral/40 text-left text-raptor-navy/70 text-xs uppercase tracking-widest">
                <tr>
                  <th className="py-2 px-3">Section</th>
                  <th className="py-2 px-3 text-right">Sum insured</th>
                  <th className="py-2 px-3 text-right">Deductible</th>
                  <th className="py-2 px-3 text-right">Premium</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <React.Fragment key={g}>
                    <tr className="bg-raptor-blue/[0.05]">
                      <td colSpan={4} className="py-1.5 px-3 text-[10px] uppercase tracking-widest text-raptor-blue font-bold">{g}</td>
                    </tr>
                    {byGroup[g].map((s) => (
                      <tr key={s.code} className="border-t border-border/60">
                        <td className="py-2 px-3">
                          <div className="font-semibold text-raptor-navy">{s.name}</div>
                          <div className="text-xs text-raptor-navy/60">{s.description}</div>
                          {s.notes && <div className="text-xs text-raptor-blue italic mt-0.5">Note: {s.notes}</div>}
                          {s.indemnity_period_months ? <div className="text-xs text-raptor-navy/50 mt-0.5">Indemnity period: {s.indemnity_period_months} months</div> : null}
                        </td>
                        <td className="py-2 px-3 text-right font-tabular">{fmtZAR(s.sum_insured_zar)}</td>
                        <td className="py-2 px-3 text-right font-tabular">{fmtZAR(s.deductible_zar)}</td>
                        <td className="py-2 px-3 text-right font-tabular">{fmtZAR(s.premium_zar)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-raptor-navy text-white">
                  <td className="py-2.5 px-3 font-bold uppercase tracking-widest text-xs">Total</td>
                  <td className="py-2.5 px-3 text-right font-tabular font-bold">{fmtZAR(cover.totals?.total_sum_insured_zar)}</td>
                  <td className="py-2.5 px-3 text-right text-white/70">—</td>
                  <td className="py-2.5 px-3 text-right font-tabular font-bold">{fmtZAR(cover.totals?.total_premium_zar)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Endorsements / warranties / notes */}
      {((cover.endorsements && cover.endorsements.length) || (cover.warranties && cover.warranties.length) || cover.notes) && (
        <div className="grid md:grid-cols-3 gap-3">
          {cover.endorsements && cover.endorsements.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Endorsements</CardTitle></CardHeader>
              <CardContent><ul className="list-disc pl-5 text-sm space-y-1 font-body">{cover.endorsements.map((e, i) => <li key={`end-${i}-${e}`}>{e}</li>)}</ul></CardContent>
            </Card>
          )}
          {cover.warranties && cover.warranties.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Warranties</CardTitle></CardHeader>
              <CardContent><ul className="list-disc pl-5 text-sm space-y-1 font-body">{cover.warranties.map((w, i) => <li key={`war-${i}-${w}`}>{w}</li>)}</ul></CardContent>
            </Card>
          )}
          {cover.notes && (
            <Card>
              <CardHeader><CardTitle className="text-base">Additional notes</CardTitle></CardHeader>
              <CardContent><div className="text-sm whitespace-pre-wrap font-body">{cover.notes}</div></CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

const PeriodCell: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
  <div className="px-4 py-3 border-r border-white/10 last:border-r-0">
    <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
    <div className="font-semibold">{value}</div>
    {sub && <div className="text-xs opacity-70 mt-0.5">{sub}</div>}
  </div>
);

export default CoverDetailPage;

interface AddendumChange { field: string; before: string; after: string; }

const AddendumModal: React.FC<{ open: boolean; onClose: () => void; coverId: string; onIssued: () => void }> = ({ open, onClose, coverId, onIssued }) => {
  const [effectiveDate, setEffectiveDate] = useState('');
  const [reason, setReason] = useState('');
  const [changes, setChanges] = useState<AddendumChange[]>([{ field: '', before: '', after: '' }]);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (!open) return null;

  const patch = (i: number, p: Partial<AddendumChange>) => {
    setChanges((cs) => cs.map((c, j) => j === i ? { ...c, ...p } : c));
  };

  const submit = async () => {
    if (!effectiveDate) { toast.error('Effective date is required.'); return; }
    const nonEmpty = changes.filter((c) => c.field.trim());
    if (nonEmpty.length === 0) { toast.error('Add at least one change row.'); return; }
    setBusy(true);
    try {
      const r = await api.post<{ ok: boolean; id: string; addendum_number: string }>(`/covers/${coverId}/addendums`, {
        effective_date: effectiveDate,
        reason,
        changes: nonEmpty,
      });
      toast.success(`Addendum ${r.data.addendum_number} issued`);
      onIssued();
      // Trigger PDF download for the issued addendum.
      try {
        const token = getAccessToken();
        const pr = await fetch(`${API_BASE}/covers/${coverId}/addendums/${r.data.id}/pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : undefined });
        if (pr.ok) {
          const blob = await pr.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a'); a.href = url; a.download = `${r.data.addendum_number}.pdf`; a.click(); URL.revokeObjectURL(url);
        }
      } catch { /* silent */ }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Addendum failed';
      toast.error(msg);
    } finally { setBusy(false); nav(0); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-raptor-navy/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="addendum-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-border/60">
          <div className="text-[10px] uppercase tracking-widest text-raptor-blue font-bold mb-1">Mid-term amendment</div>
          <h3 className="font-headline text-xl text-raptor-navy tracking-wide">Issue cover addendum</h3>
          <p className="text-xs text-raptor-navy/60 mt-1 font-body">
            An addendum is bound to the <b>current</b> rating table version and produces a short PDF referencing this cover. The parent cover remains issued.
          </p>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Effective from *</Label>
              <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} data-testid="addendum-effective-date" />
            </div>
          </div>
          <div>
            <Label>Reason for amendment</Label>
            <textarea className="w-full border border-border/70 rounded-md px-3 py-2 text-sm font-body" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Insured requested increased fire cover following stock expansion" data-testid="addendum-reason" />
          </div>
          <div>
            <Label>Changes</Label>
            <div className="space-y-2">
              {changes.map((c, i) => (
                // eslint-disable-next-line react/no-array-index-key -- editable rows without stable IDs; add/remove is append-only + filter
                <div key={`chg-${i}`} className="grid grid-cols-1 md:grid-cols-8 gap-2 items-start">
                  <Input className="md:col-span-3" placeholder="Field (e.g. Fire — Sum insured)" value={c.field} onChange={(e) => patch(i, { field: e.target.value })} data-testid={`addendum-field-${i}`} />
                  <Input className="md:col-span-2" placeholder="Before" value={c.before} onChange={(e) => patch(i, { before: e.target.value })} />
                  <Input className="md:col-span-2" placeholder="After" value={c.after} onChange={(e) => patch(i, { after: e.target.value })} />
                  <Button variant="ghost" size="sm" onClick={() => setChanges((cs) => cs.filter((_, j) => j !== i))} disabled={changes.length === 1}><Trash2 size={14} /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setChanges((cs) => [...cs, { field: '', before: '', after: '' }])} data-testid="addendum-add-change">+ Add change</Button>
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-border/60 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="gradient" onClick={() => void submit()} disabled={busy} data-testid="addendum-issue-btn">
            <ShieldCheck size={14} className="mr-1" /> {busy ? 'Issuing…' : 'Issue addendum'}
          </Button>
        </div>
      </div>
    </div>
  );
};
